import * as React from 'react';

// Utils
import { isNil, isNilOrEmpty, path } from '@src/shared/src/util/general';
import {
  canOptOutOfFlexLane,
  getTripBasketItem,
  getHotelBasketItem,
  isFlexLaneDirectionCovered,
} from '@src/shared/src/util/flexlane';
import { getTripFirstTS, getTripLastTS } from '@src/shared/src/util/trips';
import { t } from '@toolkit/util/i18n';
// Constants
import { ENVIRONMENT, DIRECTION } from '@src/shared/src/const/app';
// Actions, Models & Interfaces
import {
  BasketItemModel,
  HotelModel,
  TravelBookingFareExtraModel,
  TripModel,
  RentalModel,
} from '@src/shared/src/models';
// Components
import { Trip } from '@pod/trips/components';
import { Hotel } from '@pod/hotels/components';
import { Rental } from '@pod/rentals/components';
import CheckoutPrice from './CheckoutPrice';
import { FlexLaneCoverageSummary } from './FlexLaneCoverageSummary';
import { FlexLaneOptOutRow } from './FlexLaneOptOutRow';
// Styles

type Props = {
  outwardTrip?:TripModel;
  inboundTrip?:TripModel;
  isRoundtrip:boolean;
  basketItems:BasketItemModel[];
  bookingHotels:HotelModel[];
  bookingRentals:RentalModel[];
  hotelRoomCount?:number;
  env:ENVIRONMENT;
  searchDateOutward:Date;
  searchDateInbound:Date;
  getTariffStatus?:(tariffId:number) => string;
  getTariffLuggage?:(tariffId:number) => TravelBookingFareExtraModel;
  transportTotal:number;
  hotelTotal:number;
  rentalTotal:number;
  showItemsDetails:boolean;
  showPrices?:boolean;
  showHotelRemarksForm?:boolean;
  hotelRemarks?:string;
  onChangeHotelRemarks?:(hotelRemarks:string) => void;
};

// Only called behind `canOptOutOfFlexLane`: `t()` on a key Locize does not carry yet reports to
// Bugsnag through `missingKeyHandler`, on every call.
const directionLabel = (trip:TripModel) => t('flexLaneOptOut.label.direction', {
  defaultValue: 'FlexLane for {{from}} – {{to}}',
  from: path(['depName'], getTripFirstTS(trip.legs)),
  to: path(['arrName'], getTripLastTS(trip.legs)),
});

const roundtripLabel = () =>
  t('flexLaneOptOut.label.roundtrip', { defaultValue: 'FlexLane for outbound & return flight' });

const hotelLabel = () =>
  t('flexLaneOptOut.label.hotel', { defaultValue: 'FlexLane for this stay' });

const BookedItems:React.FC<Props> = (props) => {
  const {
    env,
    transportTotal,
    hotelTotal,
    rentalTotal,
    inboundTrip,
    outwardTrip,
    showPrices,
  } = props;

  const basketItems = props.basketItems ?? [];
  const outwardItem = outwardTrip && getTripBasketItem(basketItems, outwardTrip);
  const inboundItem = inboundTrip && getTripBasketItem(basketItems, inboundTrip);
  const isOneTicketRoundtrip = !isNil(outwardItem) && !isNil(inboundItem) &&
    !isNil(outwardItem.travelBookingItem) &&
    Number(outwardItem.travelBookingItem.id) === Number(inboundItem.travelBookingItem?.id);
  // Both directions share one Travel::BookingItem, so one row toggles both: it sits on the card
  // of a direction that is covered and names that one, never claiming to cover a leg it does not.
  const coversOutward = isFlexLaneDirectionCovered(outwardItem, DIRECTION.OUTWARD);
  const coversInbound = isFlexLaneDirectionCovered(inboundItem, DIRECTION.INBOUND);
  const sharedRowDirection = !isOneTicketRoundtrip
    ? null
    : (coversOutward && !coversInbound ? DIRECTION.OUTWARD : DIRECTION.INBOUND);
  const showOutwardRow = canOptOutOfFlexLane(outwardItem) &&
    sharedRowDirection !== DIRECTION.INBOUND;
  const showInboundRow = canOptOutOfFlexLane(inboundItem) &&
    sharedRowDirection !== DIRECTION.OUTWARD;

  return (
      <span>
        {outwardTrip &&
          <Trip
            direction={DIRECTION.OUTWARD}
            key={outwardTrip.id}
            trip={outwardTrip}
            env={env}
            searchDate={props.searchDateOutward}
            getTariffStatus={props.getTariffStatus}
            getTariffLuggage={props.getTariffLuggage}
            detailsOpened={props.showItemsDetails}
            onFetchDetail={() => {}}
            onSelectTrip={() => {}}
            onCloseDetail={() => {}}
            isRoundtrip={props.isRoundtrip}/>}

        {showOutwardRow &&
          <FlexLaneOptOutRow basketItem={outwardItem} label={directionLabel(outwardTrip)} />}

        {inboundTrip &&
          <Trip
            direction={DIRECTION.INBOUND}
            key={inboundTrip.id}
            trip={inboundTrip}
            env={env}
            searchDate={props.searchDateInbound}
            getTariffStatus={props.getTariffStatus}
            getTariffLuggage={props.getTariffLuggage}
            detailsOpened={props.showItemsDetails}
            onFetchDetail={() => {}}
            onSelectTrip={() => {}}
            onCloseDetail={() => {}}
            isRoundtrip={props.isRoundtrip}/>}

        {showInboundRow &&
          <FlexLaneOptOutRow
            basketItem={inboundItem}
            label={isOneTicketRoundtrip && coversOutward
              ? roundtripLabel()
              : directionLabel(inboundTrip)} />}

        {showPrices && (outwardTrip || inboundTrip) &&
          <CheckoutPrice
            label={t('BookedItems.label.transportTotal')}
            type='subtotal'
            price={transportTotal} />
      }

        {props.bookingRentals.map((rental:RentalModel) =>
          <Rental
            key={rental.id}
            rental={rental}
            env={props.env}
            detailsOpened={props.showItemsDetails}/>)
        }

        {showPrices && !isNilOrEmpty(props.bookingRentals) &&
          <CheckoutPrice
            label={t('BookedItems.label.rentalTotal')}
            type='subtotal'
            price={rentalTotal} />
        }

        {props.bookingHotels.map((hotel:HotelModel) => {
          const hotelItem = getHotelBasketItem(basketItems, hotel);

          return (
            <React.Fragment key={hotel.id}>
              <Hotel
                hotel={hotel}
                env={props.env}
                hotelRoomCount={props.hotelRoomCount}
                detailsOpened={props.showItemsDetails}
                onCloseDetail={() => {}}
                // getTariffStatus={props.getTariffStatus}
                onFetchDetail={() => {}}
                onSelectHotel={() => {}}
                showHotelRemarksForm={props.showHotelRemarksForm && !isNilOrEmpty(props.bookingHotels)}
                hotelRemarks={props.hotelRemarks}
                onChangeHotelRemarks={props.onChangeHotelRemarks}
              />
              {canOptOutOfFlexLane(hotelItem) &&
                <FlexLaneOptOutRow basketItem={hotelItem} label={hotelLabel()} />}
            </React.Fragment>
          );
        })}

        {showPrices && !isNilOrEmpty(props.bookingHotels) &&
          <CheckoutPrice
            label={t('BookedItems.label.hotelTotal')}
            type='subtotal'
            price={hotelTotal} />
        }

        <FlexLaneCoverageSummary basketItems={basketItems} />
      </span>
  );
};

export default BookedItems;
